import * as intrfce from '../Interfaces';
import { validateBlockedTerritory } from './blockedTerritories';

/**
 * Creates a billing address object for API submission
 */
export const createBillingAddressObject = (
  formBillingData: intrfce.iBillingFormData,
  formBillingAddress: intrfce.iAddressFormData,
  addressOptions: any,
  emailAddress: string | null
) => {
  const address: any = {
    id: formBillingData.id,
    firstName: formBillingData.firstNameBilling,
    lastName: formBillingData.lastNameBilling,
    line1: formBillingAddress.address,
    line2: formBillingAddress.line2,
    city: formBillingAddress.cityValue,
    country: formBillingAddress.countryValue,
    countryName: formBillingAddress.countryName,
    postalCode: formBillingAddress.pincode,
    phoneNumber: formBillingData.contactBilling,
    emailAddress: formBillingData.emailBilling || emailAddress,
  };

  // Include countrySubdivision based on locale configuration
  if (addressOptions.stateDropdown && formBillingAddress.stateValue != null && formBillingAddress.stateValue !== '') {
    // If stateDropdown is enabled and value exists, send the actual value
    address.countrySubdivision = formBillingAddress.stateValue;
  } else {
    // If stateDropdown is disabled or no value, send a single space
    address.countrySubdivision = ' ';
  }

  // Only include county if countyField is enabled and value exists
  if (addressOptions.countyField && formBillingAddress.county != null && formBillingAddress.county !== '') {
    address.county = formBillingAddress.county;
  }

  // Only include addressType if provided
  if (addressOptions.addressBilling) {
    address.addressType = addressOptions.addressBilling;
  }

  return address;
};

/**
 * Creates a shipping address object for API submission
 */
export const createShippingAddressObject = (
  isShipping: boolean,
  formData: intrfce.iFormContainer,
  formBillingData: intrfce.iBillingFormData,
  formShippingAddress: intrfce.iAddressFormData,
  formBillingAddress: intrfce.iAddressFormData,
  addressOptions: any,
  emailAddress: string | null
) => {
  if (isShipping) {
    const address: any = {
      id: formData.id,
      firstName: formData.firstNameShiping,
      lastName: formData.lastNameShiping,
      line1: formShippingAddress.address,
      line2: formShippingAddress.line2,
      city: formShippingAddress.cityValue,
      country: formShippingAddress.countryValue,
      countryName: formShippingAddress.countryName,
      postalCode: formShippingAddress.pincode,
      phoneNumber: formData.contactShiping,
      emailAddress: formBillingData.emailBilling || emailAddress,
    };

    // Include countrySubdivision based on locale configuration
    if (addressOptions.stateDropdown && formShippingAddress.stateValue != null && formShippingAddress.stateValue !== '') {
      // If stateDropdown is enabled and value exists, send the actual value
      address.countrySubdivision = formShippingAddress.stateValue;
    } else {
      // If stateDropdown is disabled or no value, send a single space
      address.countrySubdivision = ' ';
    }

    // Only include county if countyField is enabled and value exists
    if (addressOptions.countyField && formShippingAddress.county != null && formShippingAddress.county !== '') {
      address.county = formShippingAddress.county;
    }

    // Only include addressType if provided
    if (addressOptions.addressShipping) {
      address.addressType = addressOptions.addressShipping;
    }

    return address;
  } else {
    const address: any = {
      id: formBillingData.id,
      firstName: formBillingData.firstNameBilling,
      lastName: formBillingData.lastNameBilling,
      line1: formBillingAddress.address,
      line2: formBillingAddress.line2,
      city: formBillingAddress.cityValue,
      country: formBillingAddress.countryValue,
      countryName: formBillingAddress.countryName,
      postalCode: formBillingAddress.pincode,
      phoneNumber: formBillingData.contactBilling,
      emailAddress: formBillingData.emailBilling || emailAddress,
    };

    // Include countrySubdivision based on locale configuration
    if (addressOptions.stateDropdown && formBillingAddress.stateValue != null && formBillingAddress.stateValue !== '') {
      // If stateDropdown is enabled and value exists, send the actual value
      address.countrySubdivision = formBillingAddress.stateValue;
    } else {
      // If stateDropdown is disabled or no value, send a single space
      address.countrySubdivision = ' ';
    }

    // Only include county if countyField is enabled and value exists
    if (addressOptions.countyField && formBillingAddress.county != null && formBillingAddress.county !== '') {
      address.county = formBillingAddress.county;
    }

    // Only include addressType if provided
    if (addressOptions.addressBilling) {
      address.addressType = addressOptions.addressBilling;
    }

    return address;
  }
};

/**
 * Creates an address validation object for Google Address Validation API
 */
export const createAddressValidationObject = (
  useShipping: boolean,
  formShippingAddress: intrfce.iAddressFormData,
  formBillingAddress: intrfce.iAddressFormData,
  locale: string
) => {
  const address = useShipping ? formShippingAddress : formBillingAddress;
  
  // Build addressLines array - include line2 if present
  const addressLines = [address.address || ""];
  if (address.line2 && address.line2.trim()) {
    addressLines.push(address.line2);
  }
  
  return {
    addressLines,
    locality: address.cityValue || "",
    administrativeArea: address.stateValue || "",
    postalCode: address.pincode || "",
    regionCode: address.countryValue || "",
    languageCode: locale?.substring(0, 2) || "en",
  };
};

/**
 * Compares two addresses and returns an object with the differences
 */
export const getAddressDifferences = (
  userAddress: intrfce.iAddressFormData,
  recommendedAddress: intrfce.iAddressFormData
): Partial<intrfce.iAddressFormData> => {
  const differences: Partial<intrfce.iAddressFormData> = {};
  
  const fields: (keyof intrfce.iAddressFormData)[] = [
    'address',
    'line2',
    'cityValue',
    'stateValue',
    'pincode',
    'countryValue',
    'countryName',
    'county'
  ];
  
  fields.forEach(field => {
    const userValue = userAddress[field];
    const recommendedValue = recommendedAddress[field];
    
    if (userValue !== recommendedValue) {
      differences[field] = recommendedValue;
    }
  });
  
  return differences;
};

/**
 * Updates a saved address array by replacing or adding an address with id: 0
 */
export const updateSavedAddressWithId0 = (
  prevAddresses: intrfce.iSavedAddress[] | null,
  newAddress: intrfce.iSavedAddress
): intrfce.iSavedAddress[] => {
  if (prevAddresses) {
    const index = prevAddresses.findIndex((addr) => addr.id === 0);
    if (index !== -1) {
      // Replace existing object with id 0
      const newArray = [...prevAddresses];
      newArray[index] = newAddress;
      return newArray;
    }
    // If no object with id 0 exists, add the new one
    return [...prevAddresses, newAddress];
  } else {
    return [newAddress];
  }
};

/**
 * Converts a recommended address from Google format to form format
 */
export const convertGoogleAddressToFormFormat = (
  googleAddress: any,
  locale: string
): intrfce.iAddressFormData => {
  return {
    pincode: googleAddress.postalAddress?.postalCode || "",
    address: googleAddress.postalAddress?.addressLines?.[0] || "",
    line2: googleAddress.postalAddress?.addressLines?.[1] || "",
    cityValue: googleAddress.postalAddress?.locality || "",
    stateValue: googleAddress.postalAddress?.administrativeArea || "",
    countryName: getCountryName(googleAddress.postalAddress?.regionCode),
    countryValue: googleAddress.postalAddress?.regionCode || "",
    county: locale !== "en-us" ? googleAddress.postalAddress?.sublocality || "" : '',
  };
};

/**
 * Helper to get country name from country code
 */
const getCountryName = (countryCode: string | undefined): string => {
  // This is a simplified version. You might want to use a more comprehensive country mapping
  const countryMap: { [key: string]: string } = {
    'US': 'United States',
    'CA': 'Canada',
    'GB': 'United Kingdom',
    'DE': 'Germany',
    'FR': 'France',
    // Add more as needed
  };
  
  return countryMap[countryCode || ''] || countryCode || '';
};

/**
 * Creates a saved address entry from form data
 */
export const createSavedAddressEntry = (
  formData: intrfce.iBillingFormData | intrfce.iFormContainer,
  formAddress: intrfce.iAddressFormData,
  nickname: string,
  isShipping: boolean = false
): intrfce.iSavedAddress => {
  if (isShipping) {
    const shippingData = formData as intrfce.iFormContainer;
    return {
      id: 0,
      city: formAddress.cityValue || "",
      country: formAddress.countryValue || "US",
      countryName: formAddress.countryName || "United States",
      countrySubdivision: formAddress.stateValue || "",
      firstName: shippingData.firstNameShiping || "",
      lastName: shippingData.lastNameShiping || "",
      line1: formAddress.address || "",
      line2: formAddress.line2 || "",
      nickName: nickname,
      phoneNumber: shippingData.contactShiping || "",
      postalCode: formAddress.pincode || "",
      isDefault: false,
    };
  } else {
    const billingData = formData as intrfce.iBillingFormData;
    return {
      id: 0,
      city: formAddress.cityValue || "",
      country: formAddress.countryValue || "US",
      countryName: formAddress.countryName || "United States",
      countrySubdivision: formAddress.stateValue || "",
      firstName: billingData.firstNameBilling || "",
      lastName: billingData.lastNameBilling || "",
      line1: formAddress.address || "",
      line2: formAddress.line2 || "",
      nickName: nickname,
      phoneNumber: billingData.contactBilling || "",
      postalCode: formAddress.pincode || "",
      isDefault: false,
    };
  }
};

/**
 * Validates if an address is in a blocked territory
 * 
 * @param address - The address to validate
 * @param locale - The current locale
 * @returns Object with isBlocked flag and error message if blocked
 */
export const validateAddressTerritory = (
  address: intrfce.iAddressFormData,
  locale: string
): { isBlocked: boolean; errorMessage?: string; territoryName?: string } => {
  const { pincode, countryValue } = address;
  
  if (!pincode || !countryValue) {
    return { isBlocked: false };
  }
  
  const result = validateBlockedTerritory(pincode, countryValue, locale);
  
  if (result.isBlocked) {
    return {
      isBlocked: true,
      errorMessage: 'Sorry, this region is currently not supported.',
      territoryName: result.territoryName
    };
  }
  
  return { isBlocked: false };
};
